import { ExpandMore } from '@mui/icons-material';
import {
  List,
  ListItem,
  ListItemText,
  Accordion as MuiAccordion,
  AccordionDetails as MuiAccordionDetails,
  AccordionSummary as MuiAccordionSummary,
  Theme,
  Typography
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FC, useState } from 'react';

interface IAccordionList {
  title: string;
  items: string[];
  showShading?: boolean;
  defaultExpanded?: boolean;
  pluralItemName?: string;
}

export const AccordionList: FC<IAccordionList> = ({
  children,
  title,
  items,
  showShading = true,
  defaultExpanded = false,
  pluralItemName: itemName
}) => {
  const classes = useStyles({ showShading });

  const [isExpanded, setisExpanded] = useState(defaultExpanded);
  return (
    <MuiAccordion
      className={classes.root}
      expanded={isExpanded}
      onChange={() => setisExpanded(prev => !prev)}
    >
      <MuiAccordionSummary
        className={classes.accordion}
        expandIcon={<ExpandMore />}
        aria-controls="accordion-content"
        id="accordion-header"
      >
        <div className={classes.topPanelSummaryWrapper}>
          <Typography className={classes.title} variant="h6">
            {title}
          </Typography>
        </div>
      </MuiAccordionSummary>
      <MuiAccordionDetails className={classes.details}>
        <List
          sx={{
            listStyleType: 'disc',
            pl: 2,
            '& .MuiListItem-root': {
              display: 'list-item'
            }
          }}
        >
          {items?.length > 0 &&
            items?.map((text, index) => (
              <ListItem disablePadding key={`${title}${index}`}>
                <ListItemText primary={text} />
              </ListItem>
            ))}
          {items?.length === 0 && (
            <Typography variant="body1">
              There are no {itemName?.toLowerCase() ?? 'items'} to display.
            </Typography>
          )}
        </List>
      </MuiAccordionDetails>
    </MuiAccordion>
  );
};

const useStyles = makeStyles<Theme, { showShading: boolean }>((theme: Theme) => ({
  root: {
    maxWidth: '100%',
    border: `1px solid ${theme.palette.grey[300]}`,
    overflowX: 'hidden'
  },
  accordion: {
    padding: '0 16px',
    '&& .MuiAccordionSummary-expandIcon': {
      padding: 3
    }
  },
  title: { color: theme.palette.primary.main },

  topPanelSummaryWrapper: {
    marginTop: theme.spacing(0.5),
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    '@media (min-width: 600px)': {
      flexDirection: 'row',
      alignItems: 'center'
    },
    '&:first-child': {
      marginTop: 0
    }
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(0.5),
    backgroundColor: ({ showShading }) => (showShading ? theme.palette.grey[50] : undefined),
    borderTop: ({ showShading }) =>
      showShading ? undefined : `1px solid ${theme.palette.grey[300]}`,
    width: '100%'
  }
}));
